import { allModuleMenus, type NavigationMenuItem } from '../config/navigationMenu';
import { adminHubCategories, administrationMenuItem } from '../config/adminNavigation';
import { resolvePageTitleFromPath } from '../config/pageTitleFromPath';

export interface PageBreadcrumbItem {
  label: string;
  to?: string;
}

const MENU_PRINCIPAL_PATH = '/menu-principal';
const MENU_PRINCIPAL_LABEL = 'Menú principal';

function withMenuPrincipal(items: PageBreadcrumbItem[]): PageBreadcrumbItem[] {
  if (items.length === 0) return items;
  if (items[0]?.to === MENU_PRINCIPAL_PATH || items[0]?.label === MENU_PRINCIPAL_LABEL) {
    return items;
  }
  return [{ label: MENU_PRINCIPAL_LABEL, to: MENU_PRINCIPAL_PATH }, ...items];
}

type PathEntry = { path: string; text: string };

const SEGMENT_LABELS: Record<string, string> = {
  nuevo: 'Nuevo',
  'nuevo-pos': 'Nuevo',
  editar: 'Editar',
  ver: 'Ver',
  clasico: 'Clásico',
  info: 'Info 360',
  alta: 'Alta',
  detalle: 'Detalle',
};

function flattenMenuPaths(items: NavigationMenuItem[]): PathEntry[] {
  const out: PathEntry[] = [];
  for (const item of items) {
    out.push({ path: item.path, text: item.text });
    if (item.subItems?.length) {
      out.push(...flattenMenuPaths(item.subItems));
    }
  }
  return out;
}

function normalizePath(pathname: string): string {
  return (pathname.replace(/\/+$/, '') || '/').trim();
}

function allPathEntries(): PathEntry[] {
  const adminEntries: PathEntry[] = [
    { path: administrationMenuItem.path, text: administrationMenuItem.text },
    ...adminHubCategories.flatMap((cat) => [
      { path: `${administrationMenuItem.path}/${cat.id}`, text: cat.title },
      ...cat.items.map((item) => ({ path: item.path, text: item.text })),
    ]),
  ];
  return [...flattenMenuPaths(allModuleMenus()), ...adminEntries];
}

function labelForSegment(segment: string): string {
  const key = segment.toLowerCase();
  if (SEGMENT_LABELS[key]) return SEGMENT_LABELS[key];
  if (/^\d+$/.test(segment)) return 'Detalle';
  return segment
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Crumb con label del menú/admin y link al path. */
export function crumb(path: string): PageBreadcrumbItem {
  return {
    label: resolvePageTitleFromPath(path),
    to: path,
  };
}

/**
 * Trail automático desde la URL: Menú principal + módulo / ítems de menú que son prefijo + segmentos finales (nuevo, editar, id…).
 * Vacío en rutas raíz (dashboard, login, el propio menú principal).
 */
export function resolveBreadcrumbsFromPath(pathname: string): PageBreadcrumbItem[] {
  const normalized = normalizePath(pathname);
  if (
    normalized === '/'
    || normalized === '/dashboard'
    || normalized === '/menu-principal'
    || normalized === '/acceso-denegado'
    || normalized === '/login'
  ) {
    return [];
  }

  const entries = allPathEntries();
  const matches = entries
    .filter((e) => normalized === e.path || normalized.startsWith(`${e.path}/`))
    .sort((a, b) => a.path.length - b.path.length);

  // Deduplicar por path (mismo path puede aparecer en menú y admin)
  const seen = new Set<string>();
  const uniqueMatches: PathEntry[] = [];
  for (const m of matches) {
    if (seen.has(m.path)) continue;
    seen.add(m.path);
    uniqueMatches.push(m);
  }

  if (uniqueMatches.length === 0) {
    return withMenuPrincipal([{ label: resolvePageTitleFromPath(normalized) }]);
  }

  const items: PageBreadcrumbItem[] = uniqueMatches.map((m, index) => {
    const isLastKnown = index === uniqueMatches.length - 1 && normalized === m.path;
    return {
      label: m.text,
      to: isLastKnown ? undefined : m.path,
    };
  });

  const longest = uniqueMatches[uniqueMatches.length - 1]!;
  if (normalized.length > longest.path.length) {
    const rest = normalized.slice(longest.path.length).split('/').filter(Boolean);
    let acc = longest.path;
    rest.forEach((segment, index) => {
      acc = `${acc}/${segment}`;
      const isLast = index === rest.length - 1;
      // IDs numéricos intermedios no aportan como link útil
      if (!isLast && /^\d+$/.test(segment)) return;
      items.push({
        label: labelForSegment(segment),
        to: isLast ? undefined : acc,
      });
    });
  }

  if (items.length === 0) return [];

  // El último nunca debe tener link
  const last = items[items.length - 1]!;
  if (last.to) {
    items[items.length - 1] = { label: last.label };
  }

  return withMenuPrincipal(items);
}
